import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
// The three files a signed artefact can be downloaded as, and the one sentence
// saying which of them a checksum belongs to — written once, over there. Every
// link it renders is still a `FileLink`, which is why this file no longer needs
// one of its own: a navigation carries no Authorization header.
import { ArchivalCopy } from "./ProjectDocumentsCard";
import Countdown from "../components/Countdown";
import { Badge, Btn, Card, ConfirmDialog, Field, Input, Select, Spinner, Textarea, formatWhen, Sheet } from "../components/Shared";
import { PersonLabel } from "../components/Portal";
import { useDisplayTimezone } from "../portalConfig";
import { documentWord, isStatementOfWork, useDocumentWord } from "../documentWord";

// Quotations for one project: price them from the group's published list, send
// them for signature, and file the PDF.
//
// **One quotation is one piece of agreed work** — facility decision,
// 2026-09-16. One quotation, one deadline, one record sent at the end, and a
// project may have several running at once: two rows reading `sent` are two
// different agreements, told apart by their own numbers.
//
// **The code says `order` and a person reads `quotation`.** The `orders` table
// is the half of a quotation that has a clock — when it was agreed, how long
// the work has, how it ended, what the data and the record hang off — and the
// facility chose to keep the table, the model and the route paths rather than
// pay for a migration and a contract change over one word. So `order_id` is
// internal here and nothing on this screen prints it; the word never reaches
// the reader. Said once for the file, not once per line.
//
// The PDF matters more than it looks: deleting a project removes everything
// recorded against it, including its log, and that is only defensible because
// whoever needs the record downloaded it first — hash and signature included.

const STATUS_TONE = {
  draft: "gray", sent: "blue", accepted: "green",
  rejected: "amber", expired: "gray", withdrawn: "gray",
};

// The quotations nobody is waiting on any more, and the sentence each one gets.
//
// `awaiting` cannot answer this. It filters on "required, and has not
// accepted", so a withdrawn quotation goes on naming everybody who had not
// answered, and a rejected one names every signatory including the person who
// refused it — `status_after_decision` ends the quotation on the first refusal, so
// the others are never asked again. Keyed on the status rather than tested
// twice, because the next dead status would otherwise be a third `||` somebody
// has to notice. `expired` is deliberately absent: the sweep moves those to
// `expired` on its own schedule and the row there is a different question.
//
// A row says the word for ITS OWN kind (decision 2 of the 2026-11-03 task), so
// the sentence is built from the row rather than stored.
const OVER = {
  withdrawn: q => `This ${documentWord(q.kind)} was withdrawn and can no longer be signed.`,
  rejected: q => `This ${documentWord(q.kind)} was declined and can no longer be signed.`,
};

// An offer with no stored file, and the two ways that happens: it was sent
// before the portal froze one, or WeasyPrint was unavailable at the moment it
// was composed — a send is not allowed to depend on a renderer, so the freeze is
// best-effort by decision. The absence is stated; nothing substitutes a checksum
// over the re-rendered copy, which is a value nobody has ever been shown.
const NO_FROZEN_COPY =
  "No archival copy of this offer was kept, so there is no md5 to check it against.";

export default function QuotationsCard({ project, canManage }) {
  const zone = useDisplayTimezone();
  // The card's own heading, button and empty state follow the CURRENT setting
  // (a new one is created as that kind); everything about one row follows the
  // row's `kind`, through `rowWord`.
  const word = useDocumentWord();
  const rowWord = (q, opts) => documentWord(q?.kind, opts);
  // Whether the person reading this is one of the people who owes a signature.
  // On a small facility that is routinely the operator or the owner, which is
  // exactly the case that had no way to sign at all.
  const user = useAuth()?.user;
  // Sending or withdrawing changes what the banner counts when the person on
  // this card is also one of the signatories.
  const refreshDuties = useAuth()?.refreshDuties;
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [services, setServices] = useState([]);
  // The groups this caller may quote the project under, and the default
  // (2026-11-03): several groups work on one project and each quotes under its
  // OWN price list, numbering and terms — `GET …/issuing-groups`, the rule the
  // server applies on create. Null until it answers; then the card falls back
  // to the project's group and `canManage`, which is what it showed before.
  const [issuing, setIssuing] = useState(undefined);   // undefined: still asking
  const [issuer, setIssuer] = useState("");
  const [creating, setCreating] = useState(false);
  const [lines, setLines] = useState([{ service_id: "", qty: "1" }]);
  // A rebate, discount or subsidy. One figure over the subtotal — the answer to
  // "what was this customer given" should be a number rather than a sum, and a
  // reduction on one service is done by pricing that line.
  // What this quotation is for, in the issuer's own words. It is frozen into the
  // signed document and printed on the PDF, so it is part of what the customer
  // agrees to rather than an internal note.
  const [notes, setNotes] = useState("");
  const [discountKind, setDiscountKind] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  // The project's own people, from the endpoint the delivery-notification
  // picker already uses — and the same closed list the server now enforces for
  // signatories, so this cannot offer somebody the send would refuse.
  const [people, setPeople] = useState([]);
  const [signers, setSigners] = useState({});     // quotation id → Set of addresses
  const [notify, setNotify] = useState({});       // quotation id → bool
  // The rendered quotation, shown in a `Sheet` — which owns Escape, the focus
  // trap and focus restoration. Until 2026-09-30 this card called
  // `useModalFocus` itself, once, at mount, when the preview did not exist yet:
  // Escape closed it, but focus never moved into it and Tab was never trapped.
  const [preview, setPreview] = useState(null);
  // The draft the person is about to throw away, while the question is open.
  // A draft is DISCARDED (2026-11-03): removed with its lines and its record,
  // nothing booked, as if it had never been created — the number alone is
  // kept out of circulation. Withdraw is for an offer that was sent.
  const [discarding, setDiscarding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // The quotations, and nothing else. This briefly also read
  // GET /api/projects/{id}/orders, to turn a quotation's `order_id` into the
  // lifecycle record's number — dropped on 2026-09-16 with the word itself. The
  // route answers every record a project has ever had, unpaginated, and the
  // card beside this one already fetches and pages exactly that; asking a
  // second time for a number no longer printed here bought nothing and held
  // Send and Withdraw behind it while it arrived. A reader who has a record's
  // number finds the quotation from the other direction: each row there names its
  // quotation.
  const load = () => api.get(`/api/projects/${project.id}/quotations`)
    .then(setQuotes)
    .catch(e => setError(e.message));

  useEffect(() => {
    load();
    api.get(`/api/projects/${project.id}/issuing-groups`)
      .then(r => setIssuing(r && Array.isArray(r.groups) ? r : null))
      .catch(() => setIssuing(null));
    if (project.group_id) {
      api.get(`/api/projects/${project.id}/notification-recipients`)
        .then(setPeople).catch(() => setPeople([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, project.group_id]);

  // Whose quotation a new one is: the group picked, else the server's default,
  // else (before it answered) the project's own. Its services are the ones
  // offered — B prices from B's list, never from the project's group's.
  const issuerId = issuing === undefined ? undefined
    : issuer ? Number(issuer)
      : (issuing ? issuing.default : project.group_id) ?? null;
  const canQuote = issuing ? issuing.groups.length > 0 : (issuing === null && canManage);
  const issuerName = issuing?.groups.find(g => g.id === issuerId)?.name;
  useEffect(() => {
    if (issuerId === undefined) return undefined;
    if (!issuerId) { setServices([]); return undefined; }
    // A stale answer for a group no longer chosen must not land over the
    // chosen group's services.
    let current = true;
    api.get(`/api/groups/${issuerId}/services`)
      .then(rows => { if (current) setServices(rows); })
      .catch(() => { if (current) setServices([]); });
    return () => { current = false; };
  }, [issuerId]);
  // A row's actions are the issuing group's people's: the server says so per
  // offer (`may_act`); an older answer without it falls back to `canManage`.
  const mayAct = q => q.may_act ?? canManage;

  const create = async () => {
    setBusy(true); setError(null);
    try {
      const body = {
        items: lines.filter(l => l.service_id)
          .map(l => ({ service_id: Number(l.service_id), qty: Number(l.qty || 1) })),
      };
      if (issuing && issuerId) body.group_id = issuerId;
      if (notes.trim()) body.notes = notes.trim();
      if (discountKind) {
        body.discount = { kind: discountKind, value: discountValue, reason: discountReason };
      }
      await api.post(`/api/projects/${project.id}/quotations`, body);
      setCreating(false);
      setLines([{ service_id: "", qty: "1" }]);
      setNotes("");
      setDiscountKind(""); setDiscountValue(""); setDiscountReason("");
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Who this quotation is FOR. Always a signatory and never un-tickable: a quotation
  // the representative is not asked to sign records an agreement with somebody
  // who was not the customer. The server refuses one without them — this only
  // means nobody reaches that refusal by clicking.
  //
  // Together with the project form's refusal to let anybody represent their own
  // account, it is what stops one person being on both sides of a signature:
  // make a project for yourself, price it, send it to yourself, sign it.
  const representatives = people
    .filter(c => c.roles?.includes("Project representative") && c.email)
    .map(c => c.email);
  const isRepresentative = email => representatives.includes(email);

  const chosen = q => new Set([
    ...representatives,
    ...(signers[q.id] ?? new Set()),
  ]);

  const toggleSigner = (q, email) => setSigners(m => {
    // The representative is not a choice, so a click on their row does nothing
    // rather than silently re-adding them on the next render.
    if (isRepresentative(email)) return m;
    const next = new Set(chosen(q));
    if (next.has(email)) next.delete(email); else next.add(email);
    return { ...m, [q.id]: next };
  });

  const send = async q => {
    const to = [...chosen(q)];
    if (to.length === 0) {
      setError(`Choose at least one person to sign — a ${rowWord(q)} nobody has to agree to is not a ${rowWord(q)}.`);
      return;
    }
    setBusy(true); setError(null); setNotice(null);
    try {
      const quiet = notify[q.id] === false;
      const out = await api.post(`/api/projects/${project.id}/quotations/${q.id}/send`, {
        signatories: to.map(email => ({
          email,
          name: people.find(c => c.email === email)?.name || null,
          capacity: "customer",
        })),
        notify_signatories: !quiet,
      });
      setNotice(quiet
        ? "Recorded. No e-mail was sent — the signatures are still required."
        : out.email_sent
          ? "The signatories have been e-mailed that it is waiting for them in the portal."
          : `The ${rowWord(q)} was frozen, but the email failed: ${out.email_error}`);
      await load();
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // "Send again" on a sent quotation is a reminder, and only that (2026-09-23).
  // It used to reissue: every signatory row deleted and recreated, so somebody
  // who had already accepted lost their decision and the quotation could never
  // reach `accepted`. The server now keeps the list it was sent to, refuses a
  // different one, and itself picks who is mailed — the required signatories
  // who have not decided. So this sends back the list ON FILE, untouched, and
  // no picker state; the e-mail cannot be switched off, a reminder is an e-mail.
  const remind = async q => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const out = await api.post(`/api/projects/${project.id}/quotations/${q.id}/send`, {
        signatories: (q.signatories || []).map(s => ({
          email: s.email, name: s.name || null,
          capacity: s.capacity || "customer", required: s.required !== false,
        })),
        notify_signatories: true,
      });
      setNotice(out.email_sent
        ? "A reminder has been emailed to everyone who has not decided yet."
        : `The reminder could not be emailed: ${out.email_error}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const showDocument = async q => {
    setError(null);
    try {
      // The quotation itself travels with the render: the /document payload is
      // number, status and HTML, and the preview has to say which file to check
      // — which is the row's own `original_pdf_md5`, not anything in the HTML.
      const doc = await api.get(`/api/projects/${project.id}/quotations/${q.id}/document`);
      setPreview({ ...doc, quote: q });
    } catch (e) { setError(e.message); }
  };

  // "Sign it now": the signing page, behind the login. Until 2026-09-24 this
  // posted `/sign-link` for a token and navigated to the public page it
  // opened; there is no token any more, and the page checks on the server that
  // the signed-in account is a signatory of this quotation.
  const signNow = q => navigate(`/projects/${project.id}/quotations/${q.id}/sign`);

  const withdraw = async q => {
    // Read before the request, and the reason is not what it looks like. `q` is
    // the object this row's closure captured, so `load()` below does not mutate
    // it — it calls `setQuotes` with fresh objects and this one goes on saying
    // what it always said. Reading first is still right, because it is read
    // BEFORE `await`: the component may re-render in between and a later
    // version of this handler must not be what answers.
    const wasSent = q.status === "sent";
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.post(`/api/projects/${project.id}/quotations/${q.id}/withdraw`);
      // It is not released for a second attempt — it is over (2026-09-16).
      // One quotation is one piece of agreed work, so withdrawing takes the
      // lifecycle record with it: `withdraw_quotation` ends it as `cancelled`,
      // silently, because nobody ever agreed to it. Worth saying out loud,
      // since finishing or cancelling work that WAS agreed does send a record
      // to whoever handles the billing, and this deliberately does not.
      //
      // **Withdraw is offered for `sent` only** (2026-11-03): the server
      // answers 409 for a draft, which is discarded instead (`discard` below)
      // — a draft reached nobody, so there is nothing to take off any table.
      // The `wasSent` branch is kept rather than folded away, because the
      // handler is read before `await` and a stale closure must still say
      // something true if a row's status moved under it.
      //
      // What the reader needs is what happens to the link that is already
      // out. `withdraw_quotation` tells the signatories nothing, and
      // `decide_quotation` then refuses their link with "ask the facility
      // whether a new quotation is coming" — the person who withdrew it is the
      // only one who can answer that, and they have to know to.
      // **"may already be out", not "is already out".** The card cannot know:
      // `send` with the e-mail switched off writes to nobody — a signatory
      // then finds the quotation on this card and signs it behind the login —
      // so a `sent` quotation is not proof that anybody was written to. Saying it flatly would tell an
      // operator to chase signatories who were never contacted, which is the
      // opposite of the help this sentence exists to give.
      setNotice(`${q.number} withdrawn. ` + (wasSent
        ? "A signatory who signs in now finds it withdrawn, and the signatories are "
          + "not told — so tell whoever you sent it to yourself. "
        : "It had not been sent, so nobody was ever asked to sign it. ")
        + "Nobody agreed to it, so no record went to whoever handles the "
        + `billing — write a new ${rowWord(q)} if the work is going ahead.`);
      await load();
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Throw a DRAFT away. The maintainer, 2026-11-03: an operator entering a
  // quotation can still cancel after the preview, and it disappears completely
  // — that is not a withdrawal. `DELETE …/quotations/{id}` removes the draft,
  // its lines and its lifecycle record in one transaction and books NOTHING in
  // the ledger; the log keeps `quote.create` and gains `quote.discard`, and the
  // number is never handed out again. The server answers 409 for anything but
  // a draft, so this is offered on `draft` rows only. Asked first, through
  // `ConfirmDialog`, because the thing it does cannot be undone.
  const discard = async q => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await api.delete(`/api/projects/${project.id}/quotations/${q.id}`);
      setDiscarding(null);
      if (preview?.quote?.id === q.id) setPreview(null);
      setNotice(`${q.number} discarded. Nobody had seen it, so nothing else changes; `
        + "its number will not be used again.");
      await load();
      refreshDuties?.();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!project.group_id) return null;

  // Who the quotation would be FOR. `create_quotation` refuses a project with
  // nobody in its Representative field (409), and since 2026-10-14 a project
  // imported from PPMS with nobody picked arrives exactly like that — so the
  // common case is said here, before any line is written, and not after Create.
  // Plain text rather than `TouchReason`: that one is hidden wherever there is a
  // hover, and a tooltip is not where an operator looks for why a button is grey.
  const unrepresented = !project.representative_email;
  const noRepresentative =
    `This project has no representative, so a ${word()} cannot be written for it. `
    + "Set one in the project's Representative field first.";

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>{word({ plural: true, capital: true })}</h3>
        {canQuote && !creating && services.length > 0 && (
          <span title={unrepresented ? noRepresentative : undefined}>
            <Btn variant="secondary" disabled={unrepresented} onClick={() => setCreating(true)}
                 style={{ padding: "5px 12px", fontSize: 12 }}>New {word()}</Btn>
          </span>
        )}
      </div>

      {/* What pressing it means, which changed twice on 2026-09-16 and was
          wrong in both directions in between. The button was briefly labelled
          for the lifecycle record, over a paragraph saying a second quotation joins
          the record this project already has and is refused unless the one
          there is withdrawn first — advice `withdraw_quotation` refuses for an
          accepted quotation, on a screen that renders no Withdraw button for one.

          `open_order_for` now opens a fresh record every time, so there is
          nothing to collide with and nothing to withdraw first.

          Gated on `!creating` because it explains a BUTTON: "This writes a new
          one" points at a control, and with the form open that control has been
          replaced by the thing it opens, so the paragraph stayed behind pointing
          at nothing.

          It used to justify itself by saying the form does not explain itself —
          which was self-contradictory, since it hides exactly when the form is
          open and somebody might think they are revising the quotation on
          screen. That sentence now lives IN the form, where it is needed
          (2026-09-16). */}
      {canQuote && !creating && services.length > 0 && unrepresented && (
        <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 12px" }}>{noRepresentative}</p>
      )}
      {canQuote && !creating && services.length > 0 && !unrepresented && (
        <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 12px" }}>
          A {word()} is one piece of agreed work: its own deadline once it is
          signed, its own record when it ends. This writes a new one and changes
          nothing that is already here — a project may have several running at
          once.
        </p>
      )}

      {error && <div role="alert" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>{error}</div>}
      {notice && <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8, background: "#f3f4f6", border: "1px solid #d1d5db", fontSize: 13 }}>{notice}</div>}

      {canQuote && issuing && issuing.groups.length > 1 && !creating && (
        <div style={{ maxWidth: 320, marginBottom: 12 }}>
          {/* Asked, never guessed: an operator of several groups, none of them
              the project's, names whose quotation this is — its price list,
              numbering and terms are that group's. */}
          <Field label={`Issued by`}>
            <Select
              options={[...(issuing.default ? [] : [{ value: "", label: "— choose your group —" }]),
                        ...issuing.groups.map(g => ({ value: String(g.id), label: g.name }))]}
              value={issuer || (issuing.default ? String(issuing.default) : "")}
              onChange={e => setIssuer(e.target.value)} />
          </Field>
        </div>
      )}
      {canQuote && issuing && issuerId && issuerId !== project.group_id && (
        <p data-issuer-note style={{ color: "#6b7280", fontSize: 13, margin: "0 0 12px" }}>
          You quote this project under {issuerName || "your group"}: its price list, its
          numbering and its terms. What it issues stays {issuerName || "your group"}'s — its group
          admin agrees the work is finished and its billing address receives the record.
        </p>
      )}
      {canQuote && issuerId && services.length === 0 && (
        <p style={{ color: "#6b7280", fontSize: 13, margin: "0 0 8px" }}>
          {issuerName ? `${issuerName} has` : "This group has"} no services yet, so nothing can be
          priced. Add them in Facility settings.
        </p>
      )}

      {creating && (
        <div style={{ marginBottom: 18, padding: "14px 16px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          {/* Every row labelled, every row removable, and "Add another line" on
              a row of its own. Labels used to appear on the first row only,
              "Add line" was a 12px button beside "Create", and a row once added
              could not be deleted — so a second line looked like a second
              quotation, and one dev project ended up with three. */}
          {/* The one sentence the paragraph above cannot deliver, because it is
              hidden while this form is open — and this is the moment somebody
              could believe they are editing the quotation on screen rather than
              writing another one beside it. */}
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 10px" }}>
            A new {word()}. It does not change any {word()} already here.
          </p>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Lines ({lines.length})
          </div>
          {lines.map((line, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 220px" }}>
                <Field label="Service">
                  <Select
                    options={[{ value: "", label: "— choose a service —" },
                              ...services.map(sv => ({ value: String(sv.id), label: sv.name }))]}
                    value={line.service_id}
                    onChange={e => setLines(ls => ls.map((l, n) => n === i ? { ...l, service_id: e.target.value } : l))} />
                </Field>
              </div>
              <div style={{ width: 100 }}>
                <Field label="Quantity">
                  <Input type="number" min="0" step="any" value={line.qty}
                    onChange={e => setLines(ls => ls.map((l, n) => n === i ? { ...l, qty: e.target.value } : l))} />
                </Field>
              </div>
              <button type="button" aria-label={`Remove line ${i + 1}`}
                      disabled={lines.length === 1}
                      onClick={() => setLines(ls => ls.filter((_, n) => n !== i))}
                      style={{ marginBottom: 6, background: "none", border: "1px solid #d1d5db",
                               borderRadius: 6, width: 30, height: 30,
                               cursor: lines.length === 1 ? "default" : "pointer",
                               color: lines.length === 1 ? "#d1d5db" : "#6b7280",
                               fontFamily: "inherit", fontSize: 16, lineHeight: 1 }}>×</button>
            </div>
          ))}

          <div style={{ marginBottom: 14 }}>
            <Btn variant="secondary"
                 onClick={() => setLines(ls => [...ls, { service_id: "", qty: "1" }])}>
              + Add another line
            </Btn>
          </div>

          {/* No "valid until" here any more: the deadline is the group's
              setting, so one facility has one answer and an operator cannot
              shorten it on a single quotation by accident. */}
          {/* The VAT tick-box was here and is gone (2026-09-16). It was already
              not a number — the rate is the facility's, set once by a group
              admin, because a rate typed per quotation is a rate that eventually
              gets typed wrong on the one that gets signed. The tick was the
              same argument one step further along: whether tax applies at all
              is a fact about what kind of customer this is, which the
              affiliation records, not a choice for whoever is filling in the
              form. The server now always reads `affiliation.vat_rate`, and
              refuses a quotation whose affiliation has none — set it to 0 for a
              customer who is not charged. */}

          <Field
            label="Notes (optional)"
            hint={`What this ${word()} is for — the work, the grant, the conversation it came out of. Printed on the ${word()} and frozen into the document that gets signed, so write it for the customer to read.`}
          >
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>

          {/* The reduction. Off unless somebody chooses one, and the reason is
              required by the server when there is one — it is printed on the
              quotation, so a discount nobody can explain would go out on the
              document. VAT is charged on what is left, which is what a discount
              means for tax; the totals below show the net. */}
          <div style={{ marginBottom: 14 }}>
            <Field label="Discount">
              <Select value={discountKind} onChange={e => setDiscountKind(e.target.value)}
                      options={[{ value: "", label: "None" },
                                { value: "percent", label: "Percentage of the subtotal" },
                                { value: "amount", label: "Fixed amount" }]} />
            </Field>
            {discountKind && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                <Field label={discountKind === "percent" ? "Per cent" : "Amount"}>
                  <Input value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                         inputMode="decimal" style={{ width: 110 }} />
                </Field>
                <Field label="Reason">
                  <Input value={discountReason} onChange={e => setDiscountReason(e.target.value)}
                         placeholder={`Printed on the ${word()}`} style={{ minWidth: 240 }} />
                </Field>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Btn onClick={create} disabled={busy || !lines.some(l => l.service_id)}>
              {busy ? <><Spinner /> Pricing…</> : `Create with ${lines.filter(l => l.service_id).length} line(s)`}
            </Btn>
            <Btn variant="secondary" onClick={() => setCreating(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* The quotation as it will be filed, rendered by the same function the PDF
          uses — a preview assembled anywhere else is a second implementation
          that can disagree with what is actually signed. */}
      {preview && (
        <Sheet label={`${rowWord(preview.quote, { capital: true })} ${preview.number}`} onClose={() => setPreview(null)}
               maxWidth={860} variant="full" zIndex={50}
               style={{ borderRadius: 10, maxHeight: "88vh", padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
              <strong style={{ fontSize: 15 }}>{preview.number}</strong>
              <Btn variant="secondary" onClick={() => setPreview(null)}>Close</Btn>
            </div>
            <div data-html dangerouslySetInnerHTML={{ __html: preview.html }} />
            {/* **What you are looking at is not the file to archive.** The
                preview is the offer with whatever signatures it now carries,
                re-rendered on every call; the original is the one frozen when
                the document was composed, and the only one whose md5 this
                portal has ever published. Said here because this is where
                somebody decides what to keep. */}
            {preview.quote && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                <ArchivalCopy
                  path={`/api/projects/${project.id}/quotations/${preview.quote.id}/pdf`}
                  md5={preview.quote.original_pdf_md5}
                  sha256={preview.quote.document_hash}
                  absent={NO_FROZEN_COPY}
                  defaultLabel="With signatures"
                  merged={false} />
              </div>
            )}
            {/* The preview is where a draft is read before it goes out, so
                the two things one does next sit here too (2026-11-03): send
                it, or throw it away. Draft only — a sent offer's preview is a
                record, and its Withdraw stays on the row where the signatory
                list it affects is shown. Send uses the row's own picker state
                (`chosen`), so what the Sheet sends is what the row shows ticked. */}
            {preview.quote && mayAct(preview.quote) && preview.quote?.status === "draft" && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                <Btn onClick={() => { const q = preview.quote; setPreview(null); send(q); }}
                     disabled={busy || people.filter(c => c.email).length === 0}>
                  Send for signature
                </Btn>
                <Btn variant="secondary" onClick={() => setDiscarding(preview.quote)} disabled={busy}>
                  Discard
                </Btn>
              </div>
            )}
        </Sheet>
      )}

      {/* The question before a draft goes (2026-11-03). Above the preview
          (`zIndex` 50) so it can be asked from inside it; no typed word, since
          a draft that reached nobody is not a project's worth of data — the
          named number and the one sentence of consequence are the confirmation. */}
      {discarding && (
        <ConfirmDialog
          title={isStatementOfWork(discarding.kind)
            ? `Discard draft ${rowWord(discarding)} ${discarding.number}?`
            : `Discard draft ${discarding.number}?`}
          confirmLabel="Discard"
          busyLabel="Discarding…"
          busy={busy}
          onConfirm={() => discard(discarding)}
          onClose={() => setDiscarding(null)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            Nobody has seen it; it will be removed and its number not reused.
          </p>
        </ConfirmDialog>
      )}

      {quotes === null ? <div style={{ color: "#6b7280" }}><Spinner /> Loading…</div>
        : quotes.length === 0 ? <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>No {word({ plural: true })} yet.</p>
        : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {quotes.map(q => (
            <div key={q.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <strong style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{q.number}</strong>
                <Badge color={STATUS_TONE[q.status] || "gray"}>{q.status}</Badge>
                {q.kind === "statement_of_work" && <Badge color="blue">Statement of Work</Badge>}
                {/* Only when the server sent one: on a Statement of Work a
                    member's payload carries no money keys at all (2026-10-28). */}
                {q.total != null && <span style={{ fontSize: 13 }}>{q.total} {q.currency}</span>}
                {/* Shown where the price is, because a total that is lower than
                    the price list explains itself here or gets queried later. */}
                {q.discount_kind && q.discount_amount != null && (
                  <span style={{ fontSize: 12, color: "#b45309" }}>
                    {`less ${q.discount_amount} ${q.currency}`
                     + (q.discount_reason ? ` — ${q.discount_reason}` : "")}
                  </span>
                )}
                <span style={{ color: "#6b7280", fontSize: 12 }}>valid until {q.valid_until}</span>
                {/* Only while it is actually waiting on somebody. A countdown
                    on a quotation that has been accepted is noise. */}
                {q.status === "sent" && (
                  <Countdown until={q.expires_at} zone={zone} expiredLabel="expired — awaiting the sweep" />
                )}
                <span style={{ flex: 1 }} />
              </div>

              {/* Two files, not three: the merged copy — the original with the
                  signature page appended — exists for documents and not yet for
                  quotations, and the route answers 409 saying so. A link whose
                  only outcome is that sentence is worse than no link. */}
              <div style={{ marginTop: 8 }}>
                <ArchivalCopy path={`/api/projects/${project.id}/quotations/${q.id}/pdf`}
                              md5={q.original_pdf_md5} sha256={q.document_hash}
                              absent={NO_FROZEN_COPY}
                              defaultLabel="With signatures" merged={false} />
              </div>

              {/* A quotation in one of those states is over, and the payload does
                  not say so: `awaiting` answers "required, and has not
                  accepted", which stays true after a withdrawal and after a
                  refusal — deliberately, because six backend readers depend on
                  that meaning. So the quotation's own status is consulted here. The
                  line matters as much as the missing button: a row that merely
                  loses its controls reads as a rendering fault. */}
              {OVER[q.status] ? (
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  {OVER[q.status](q)}
                </div>
              ) : q.awaiting?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#b45309" }}>
                  {/* Each `Name <email>`: two signatories may share a name (PERSON). */}
                  <div>Waiting for {q.awaiting.map((a, i) => (
                    <span key={a.user_id ?? a.email ?? i}>{i > 0 && ", "}<PersonLabel name={a.name} email={a.email} /></span>
                  ))}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    {/* By account, as the server signs (2026-09-24): an address
                        can be shared, or differ in case, from the account's. */}
                    {user?.id != null && q.awaiting.some(a => a.user_id === user.id) && (
                      <Btn onClick={() => signNow(q)}>Sign it now</Btn>
                    )}
                  </div>
                </div>
              )}

              {q.signatures?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                  {q.signatures.map((s, i) => (
                    <div key={i}>
                      {s.decision} by {s.signer_name} ({s.signer_email}) · {formatWhen(s.decided_at, { zone })}
                      {s.ip ? ` · ${s.ip}` : ""}
                      {!s.matches_current && (
                        <span style={{ color: "#b45309" }}> · the {rowWord(q)} has changed since this was signed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {mayAct(q) && ["draft", "sent"].includes(q.status) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
                  {/* The project's own people, ticked. It was a single free user
                      search — which could name somebody the server would refuse,
                      and could only ever name one, though a quotation to a research
                      group routinely needs the PI and whoever holds the budget. */}
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                    Who must sign
                  </div>
                  {q.status === "sent" ? (
                    // Read-only once sent: the list is fixed, and the server
                    // refuses a different one (Withdraw and a new quotation is
                    // how the list changes). Somebody who has decided is shown
                    // as decided, never as a box to tick — they are never
                    // reminded, whatever is sent.
                    <ul aria-label="Signatories" style={{ listStyle: "none", padding: 0, margin: "0 0 10px", display: "flex", flexDirection: "column", gap: 4 }}>
                      {(q.signatories || []).map(s => (
                        <li key={s.email} style={{ fontSize: 13 }}>
                          {s.name || s.email}
                          <span style={{ color: "#6b7280" }}> · {s.email}</span>
                          {" · "}
                          {s.decision ? (
                            <span style={{ color: s.decision === "accepted" ? "#047857" : "#b45309" }}>
                              {s.decision}{s.decided_at ? ` ${formatWhen(s.decided_at, { zone })}` : ""}
                            </span>
                          ) : s.required === false ? (
                            <span style={{ color: "#6b7280" }}>for information</span>
                          ) : (
                            <span style={{ color: "#b45309" }}>not decided yet</span>
                          )}
                        </li>
                      ))}
                      <li style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        The list is fixed now that the {rowWord(q)} has been sent. A reminder
                        goes to everyone above who has not decided yet; to change who
                        signs, withdraw this {rowWord(q)} and create a new one.
                      </li>
                    </ul>
                  ) : people.filter(c => c.email).length === 0 ? (
                    <p style={{ fontSize: 12, color: "#b45309", margin: "0 0 10px" }}>
                      Nobody on this project has an e-mail address yet — add a
                      representative or a member before sending a {rowWord(q)}.
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                      {people.filter(c => c.email).map(c => (
                        <label key={c.email} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 13 }}>
                          {/* Disabled for the representative: it says the tick
                              is not a choice, rather than letting somebody
                              click it and watch nothing happen. */}
                          <input type="checkbox" checked={chosen(q).has(c.email)}
                                 disabled={isRepresentative(c.email)}
                                 title={isRepresentative(c.email)
                                   ? `The ${rowWord(q)} is for this person, so they always sign it.`
                                   : undefined}
                                 onChange={() => toggleSigner(q, c.email)} />
                          <span>
                            {c.name || c.email}
                            <span style={{ color: "#6b7280" }}> · {c.email}</span>
                            {c.roles?.length > 0 && (
                              <span style={{ color: "#6b7280", fontSize: 12 }}> · {c.roles.join(", ")}</span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.status !== "sent" && (
                  <label style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 13, marginBottom: 10 }}>
                    <input type="checkbox" checked={notify[q.id] !== false}
                           onChange={e => setNotify(m => ({ ...m, [q.id]: e.target.checked }))} />
                    <span>
                      E-mail the signatories
                      <span style={{ color: "#6b7280", fontSize: 12 }}>
                        {" "}· turn off to send no e-mail. The signatures are still
                        required: every signatory signs in to the portal and finds
                        the {rowWord(q)} on this page, and the e-mail only tells them it is
                        waiting — use Send reminder when they should get it.
                      </span>
                    </span>
                  </label>
                  )}

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {/* Read it before sending it: there was no way to see a
                        quotation without downloading a PDF. */}
                    <Btn variant="secondary" onClick={() => showDocument(q)}>Preview</Btn>
                    {q.status === "sent" ? (
                      <Btn onClick={() => remind(q)}
                           disabled={busy || !(q.awaiting?.length > 0)}>
                        Send reminder
                      </Btn>
                    ) : (
                      <Btn onClick={() => send(q)}
                           disabled={busy || people.filter(c => c.email).length === 0}>
                        Send for signature
                      </Btn>
                    )}
                    {/* Two ways out, and they are not two strengths of one
                        thing (2026-11-03). A DRAFT is discarded: removed with
                        its lines and its record, nothing booked, the number
                        burnt. A SENT offer is withdrawn: it stays as evidence,
                        and its record is booked as an unagreed ending. The
                        server refuses each on the other's status (409), so a
                        row never offers a button whose only outcome is a refusal. */}
                    {q.status === "sent" ? (
                      <Btn variant="secondary" onClick={() => withdraw(q)} disabled={busy}>
                        Withdraw
                      </Btn>
                    ) : (
                      <Btn variant="secondary" onClick={() => setDiscarding(q)} disabled={busy}>
                        Discard
                      </Btn>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
